import styles from "./PastOrder.module.css";
import { error as logError } from "../../../utils/logger";
import { useEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, CircleX } from "lucide-react";
import { useOrderContext } from "../../../context/OrderContext";
import {
    denormalizeDate,
    generateTimes,
    addMinutesToTime,
    getDayHours,
    WEEK_DAY_FULL,
    isDateFarEnough,
    isAvailableOn,
    normalizeDate,
    filterDatesOnOrAfterEffectiveTomorrow,
    isTimeBetween,
} from "../../../utils/calendar";
import { usePastOrderContext } from "../../../context/PastOrderContext";
import { useDropdown } from "../../../hooks/useDropdown";
import { defaultMaxTime, defaultMinTime, defaultOrderTime } from "../../../constants/times";
import { usePreferences } from "../../../context/PreferencesContext";
import { CartItem } from "../../../types/cart.types";
import { groupCartData, cartChange, resolveProductByCategory } from "../../../utils/cart";
import type { Variants, Transition } from "motion/react";
import SecondaryButton from "../../Button/SecondaryButton/SecondaryButton";
import CartProductCard from "../../CartProductCard/CartProductCard";
import { AnimatePresence, LayoutGroup } from "motion/react";
import * as motion from "motion/react-client";
import { DayKey } from "../../../types/product.types";
import { useCartContext } from "../../../context/CartContext";
import useEnsureCatalogData from "../../../hooks/useEnsureCatalogData";
import { useTimeModal } from "../../../context/TimeModalContext";
import { getAdvanceDays } from "../../../utils/products";
import PastOrderSummary from "./PastOrderSummary";
import PastOrderErrorTooltip from "./PastOrderErrorTooltip";

/**
 * A popup component for re-ordering past orders.
 */
const PastOrderPopup = () => {
    const { t } = useTranslation(); // Translation
    const { selectedCurrency } = usePreferences(); // Current selected currency
    const { allProducts, packages, items, activities } = useEnsureCatalogData({
        productSource: "allProducts",
    });

    const { openedPastOrderCard, closePastOrder, pastOrderOpen } = usePastOrderContext();
    const { lastTime } = useTimeModal(); // you only use lastTime here
    const { cartData, reloadCart } = useCartContext();
    const { orderData } = useOrderContext();

    const cartDatesISO = useMemo(() => {
        return filterDatesOnOrAfterEffectiveTomorrow(
            (cartData?.selected_dates ?? []).map(normalizeDate)
        );
    }, [cartData?.selected_dates]);

    const defaultDate = cartDatesISO[0] ?? orderData.selectedDate;

    const [selectedDate, setSelectedDate] = useState(defaultDate);
    const [selectedTime, setSelectedTime] = useState(openedPastOrderCard?.time || defaultOrderTime); // "HH:mm - HH:mm"

    // Dropdowns
    const { ref, isOpen: selectOpen, setIsOpen: setSelectOpen } = useDropdown();
    const { ref: dateRef, isOpen: dateOpen, setIsOpen: setDateOpen } = useDropdown();

    // Time options
    let timeOptions: string[] = [];
    const [options, setOptions] = useState<string[]>(timeOptions);
    if (timeOptions.length < 1) {
        timeOptions = generateTimes(15, defaultMinTime, defaultMaxTime);
    }

    // Reset date/time on open/close
    useEffect(() => {
        const times = generateTimes(15, defaultMinTime, defaultMaxTime);
        setOptions(times);
        setSelectedTime(openedPastOrderCard?.time || defaultOrderTime);
        setSelectedDate(cartDatesISO[0] ?? orderData.selectedDate);
    }, [pastOrderOpen, openedPastOrderCard, orderData.selectedDate, cartDatesISO]);

    // Grouped cart type
    type GroupedCart = {
        [date: string]: {
        [time: string]: CartItem[];
        };
    };

    // Grouped data from past order (by date/time)
    const [groupedCart, setGroupedCart] = useState<GroupedCart | null>(null);

    useEffect(() => {
        if (!openedPastOrderCard) {
        setGroupedCart(null);
        return;
        }
        setGroupedCart(groupCartData(openedPastOrderCard.order ?? null));
    }, [openedPastOrderCard]);

    // Local editing of amounts / removing items inside a block
    const [selectedPairIndex, setSelectedPairIndex] = useState<number>(0);

    const startMinutes = (range: string) => {
        const [start] = range.split(" - ");
        const [h, m] = start.split(":").map(Number);
        return h * 60 + m;
    };

    // Build list of date-time pairs => {date, time}
    const pairs = useMemo(() => {
        if (!groupedCart) return [];
        return Object.entries(groupedCart)
        .sort(([da], [db]) => da.localeCompare(db)) // date ASC
        .flatMap(([date, times]) =>
            Object.keys(times)
            .sort((a, b) => startMinutes(a) - startMinutes(b)) // time ASC
            .map((time) => ({ date, time }))
        );
    }, [groupedCart]);

    // Keep selectedPairIndex in range & auto-close when no blocks left
    useEffect(() => {
        if (!pastOrderOpen) return;
        if (!groupedCart) return;

        if (pairs.length === 0) {
        closePastOrder();
        return;
        }
        if (selectedPairIndex >= pairs.length) {
        setSelectedPairIndex(pairs.length - 1);
        }
    }, [pairs.length, selectedPairIndex, closePastOrder, groupedCart, pastOrderOpen]);

    // Select matching pair on popup open
    useEffect(() => {
        if (!openedPastOrderCard || pairs.length === 0) return;
        const idx = pairs.findIndex(
        (p) => p.date === openedPastOrderCard.date && p.time === openedPastOrderCard.time
        );
        setSelectedPairIndex(idx >= 0 ? idx : 0);
    }, [openedPastOrderCard, pairs.length, pairs]);

    const selectedPair = pairs[selectedPairIndex];

    const displayDate = selectedPair?.date ?? defaultDate;
    const displayTime = selectedPair?.time ?? "";

    useEffect(() => {
        setSelectedTime(displayTime);
    }, [displayTime]);

    // Visible products in the selected block
    const visibleProducts = useMemo(() => {
        if (!selectedPair || !groupedCart) return [];
        const bucket = groupedCart[selectedPair.date]?.[selectedPair.time] ?? [];
        return bucket.filter((item) => item.category !== "service");
    }, [groupedCart, selectedPair]);

    // Group price
    const visiblePrice = useMemo(() => {
        let price = 0;
        visibleProducts.forEach((element) => {
        if (element.category === "sales") {
            const product = allProducts.find((p) => p.id === Number(element.id));
            if (product) {
            price += element.amount * product.pricing.price_without_tax;
            }
        } else if (element.category === "package") {
            const product = packages.find((p) => p.id === Number(element.id));
            if (product) {
            price += element.amount * (product.pricing?.price_without_tax ?? 1);
            }
        } else if (element.category === "storage") {
            const product = items.find((p) => p.id === Number(element.id));
            if (product) {
            price += element.amount * (product.pricing?.price_without_tax ?? 1);
            }
        } else if (element.category === "activities") {
            // no pricing
        }
        });
        return price;
    }, [visibleProducts, allProducts, packages, items]);

    const transport = 300;

    // Prev/next block
    const hasPrev = selectedPairIndex > 0;
    const hasNext = selectedPairIndex < pairs.length - 1;

    const [error, setError] = useState<CartItem[]>([]);

    const goPrev = () => {
        if (!hasPrev) return;
        setSelectedPairIndex((i) => i - 1);
        setError([]);
    };

    const goNext = () => {
        if (!hasNext) return;
        setSelectedPairIndex((i) => i + 1);
        setError([]);
    };

    // Local group edit (quantity & X button)
    const handleGroupedCart = (
        id: number,
        amount: number,
        type: string,
        timeArg?: string,
        dateArg?: string
    ) => {
        const date = dateArg ?? selectedPair?.date;
        const time = timeArg ?? selectedPair?.time;
        if (!date || !time) return;

        setGroupedCart((prev) => {
        if (!prev) return prev;
        const day = prev[date];
        if (!day) return prev;
        const bucket = day[time];
        if (!bucket) return prev;

        const index = bucket.findIndex((it) => it.id === id && it.category === type);
        if (index === -1) return prev;

        const newBucket =
            amount > 0
            ? bucket.map((item, i) => (i === index ? { ...item, amount } : item))
            : bucket.filter((_, i) => i !== index);

        if (newBucket.length === 0) {
            const { [time]: _rm, ...restTimes } = day;
            if (Object.keys(restTimes).length === 0) {
            const { [date]: _rmDay, ...restDates } = prev;
            return restDates as GroupedCart;
            }
            return { ...prev, [date]: restTimes };
        }

        return { ...prev, [date]: { ...day, [time]: newBucket } };
        });
    };

    // Product lookup helper using consolidated utility
    const productLookup = { allProducts, packages, items, activities };
    const resolveProduct = (el: CartItem) => resolveProductByCategory(el, productLookup);

    const getLeadTimeDays = (product: any): number | null => {
        const raw = product?.order_in_advance;
        if (raw == null || String(raw).trim() === "") return null;
        return getAdvanceDays(product, 0);
    };

    // All checks – use explicit targetDate + targetStart
    const passesRules = (el: CartItem, product: any, targetDate: string, targetStart: string) => {
        // Lead time (order_in_advance)
        const byLeadTime = (() => {
        const gap = getLeadTimeDays(product);
        return gap == null ? true : isDateFarEnough(gap, targetDate);
        })();

        // Weekday (not for storage / activities)
        const byWeekday = (() => {
        if (el.category === "storage" || el.category === "activities") return true;
        const { day } = getDayHours(targetDate);
        const weekday = WEEK_DAY_FULL[day - 1] as DayKey;
        return isAvailableOn(product, weekday);
        })();

        // Time window (order_from / order_to)
        const byTime = (() => {
        return isTimeBetween(targetStart, product.order_from, product.order_to);
        })();

        return el.category === "storage" || el.category === "activities"
        ? byLeadTime && byTime
        : byLeadTime && byWeekday && byTime;
    };

    // Add whole block to cart
    const handleCartAdd = async () => {
        if (!selectedPair) return;

        const failed: CartItem[] = [];
        let anySuccess = false;

        const targetDate = selectedDate;
        const targetRange = selectedTime; // defaultOrderTime
        const targetStart = targetRange.split(" - ")[0]; // "7:00"

        for (const element of visibleProducts) {
        if (element.category === "service") continue;

        const product = resolveProduct(element);
        if (!product) {
            failed.push(element);
            continue;
        }

        const okByRules = passesRules(element, product, targetDate, targetStart);
        if (!okByRules) {
            failed.push(element);
            continue;
        }

        try {
            await cartChange(
            cartData,
            element.id,
            targetDate,
            targetRange,
            element.amount,
            element.category,
            targetDate,
            targetRange,
            undefined // don't reload on each item
            );
            anySuccess = true;
        } catch (err) {
            logError("Failed to add item from past order:", { element, err });
            failed.push(element);
        }
        }

        if (anySuccess) {
        reloadCart();
        }

        setError(failed.length > 0 ? failed : []);

        setGroupedCart((prev) => {
        if (!prev) return prev;

        const date = selectedPair.date;
        const time = selectedPair.time;
        const day = prev[date];
        if (!day) return prev;

        if (failed.length === 0) {
            const { [time]: _rm, ...restTimes } = day;
            if (Object.keys(restTimes).length === 0) {
            const { [date]: _rmDay, ...restDates } = prev;
            return restDates as GroupedCart;
            }
            return { ...prev, [date]: restTimes };
        }

        return { ...prev, [date]: { ...day, [time]: failed } };
        });
    };

    // Animations
    const first = useRef(true);
    useEffect(() => {
        first.current = false;
    }, []);

    const prevIndexRef = useRef(selectedPairIndex);
    const direction = selectedPairIndex > prevIndexRef.current ? 1 : -1;
    useEffect(() => {
        prevIndexRef.current = selectedPairIndex;
    }, [selectedPairIndex]);

    const spring: Transition = { type: "spring", stiffness: 500, damping: 30 };
    const list: Variants = {
        hidden: { opacity: 0, y: -8 },
        show: { opacity: 1, y: 0, transition: { staggerChildren: 0.02 } },
        exit: { opacity: 0, y: -8, transition: { duration: 0.3 } },
    };
    const listItem: Variants = {
        hidden: { opacity: 0, y: -8 },
        show: { opacity: 1, y: 0, transition: spring },
        exit: { opacity: 0, y: -8, transition: { duration: 0.4 } },
    };

    const spring2: Transition = { type: "spring", stiffness: 600, damping: 40, mass: 0.7 };
    const panelVariants: Variants = {
        enter: (dir: number) => ({
        x: dir > 0 ? 60 : -60,
        opacity: 0,
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        }),
        center: {
        x: 0,
        opacity: 1,
        position: "relative",
        transition: spring2,
        },
        exit: (dir: number) => ({
        x: dir > 0 ? -60 : 60,
        opacity: 0,
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        transition: spring2,
        }),
    };

    const tooltipVariants = {
        hidden: { opacity: 0, y: -8, scale: 0.98 },
        show: { opacity: 1, y: 0, scale: 1, transition: spring },
        exit: { opacity: 0, y: -8, scale: 0.98, transition: spring },
    };

    return (
        <>
        {openedPastOrderCard !== undefined && (
            <div className={styles.PastOrder}>
            <AnimatePresence initial={false}>
                {error.length > 0 && (
                <PastOrderErrorTooltip
                    errors={error}
                    onClose={() => setError([])}
                    cartDatesISO={cartDatesISO}
                    allProducts={allProducts}
                    packages={packages}
                    items={items}
                    activities={activities}
                    tooltipVariants={tooltipVariants}
                />
                )}
            </AnimatePresence>

            <div className={styles.PastOrderBackground} onClick={() => closePastOrder()}></div>

            <div className={styles.PastOrderPopup}>
                <button className={styles.Close} onClick={() => closePastOrder()}>
                <CircleX />
                </button>

                <p className={styles.PastOrderPopupTitle}>{t("buttons.order")}</p>

                <div className={styles.PastOrderContent}>
                <div className={styles.PastOrderContentWrap}>
                    {/* LEFT: block detail */}
                    <div className={styles.PastOrderSlideViewport}>
                    <AnimatePresence initial={false} mode="sync" custom={direction}>
                        <motion.div
                        key={selectedPairIndex}
                        custom={direction}
                        variants={panelVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        layout="size"
                        className={styles.ProductWrap}
                        >
                        <div className={styles.ProductSection}>
                            <div className={styles.ProductHeader}>
                            <p className={styles.ProductTitle}>{t("account.orders.block_detail")}</p>
                            <div className={styles.ProductSubtitle}>
                                <p>{displayDate ? denormalizeDate(displayDate) : null}</p>
                                <p>{displayTime}</p>
                            </div>
                            </div>

                            <div className={styles.ProductContent}>
                            {visibleProducts.map((prod) => {
                                const product = resolveProduct(prod);
                                if (!product) return null;
                                return (
                                    <CartProductCard
                                        key={`${prod.category}-${prod.id}`}
                                        cartItem={prod}
                                        product={product}
                                        selectedCurrency={selectedCurrency}
                                        onRemove={() => handleGroupedCart(prod.id, 0, prod.category)}
                                        onAmountChange={(newAmount) => handleGroupedCart(prod.id, newAmount, prod.category)}
                                    />
                                );
                            })}
                            </div>

                            <PastOrderSummary
                                visiblePrice={visiblePrice}
                                transport={transport}
                                selectedCurrency={selectedCurrency}
                            />
                        </div>
                        </motion.div>
                    </AnimatePresence>
                    </div>

                    {/* RIGHT: date + time pick */}
                    <div className={styles.DateSection}>
                    <p className={styles.DateSectionTitle}>{t("account.orders.pick_date")}</p>

                    <div className={styles.TimePickWrapper} ref={ref}>
                        <div className={styles.TimePick} onClick={() => setSelectOpen((prev) => !prev)}>
                        {selectedTime && <p>{selectedTime}</p>}
                        </div>

                        <AnimatePresence initial={false}>
                        {selectOpen && (
                            <motion.ul
                            variants={list}
                            initial="hidden"
                            animate="show"
                            exit="exit"
                            className={styles.PossibleTimes}
                            >
                            {options.map((time) => {
                                const compTime = `${time} - ${addMinutesToTime(time, 45)}`;
                                return (
                                <motion.li
                                    variants={listItem}
                                    className={`${styles.TimeOption} ${
                                    selectedTime === compTime ? styles.SelectedTime : ""
                                    } ${lastTime === compTime ? styles.LastTime : ""}`}
                                    key={compTime}
                                    onClick={() => {
                                    setSelectedTime(compTime);
                                    setSelectOpen(false);
                                    }}
                                >
                                    <p>{compTime}</p>
                                </motion.li>
                                );
                            })}
                            </motion.ul>
                        )}
                        </AnimatePresence>
                    </div>

                    <div className={styles.DatePickWrapper} ref={dateRef}>
                        <div className={styles.DatePick} onClick={() => setDateOpen((prev) => !prev)}>
                        {selectedDate && <p>{denormalizeDate(selectedDate)}</p>}
                        </div>

                        <AnimatePresence initial={false}>
                        {dateOpen && (
                            <motion.div
                            variants={list}
                            initial="hidden"
                            animate="show"
                            exit="exit"
                            className={styles.DatePopover}
                            >
                            {cartDatesISO.map((date, index) => (
                                <motion.li
                                variants={listItem}
                                className={`${styles.DateOption} ${
                                    selectedDate === date ? styles.SelectedDate : ""
                                }`}
                                key={`${date}-${index}`}
                                onClick={() => {
                                    setSelectedDate(date);
                                    setDateOpen(false);
                                }}
                                >
                                <p>{denormalizeDate(date)}</p>
                                </motion.li>
                            ))}
                            </motion.div>
                        )}
                        </AnimatePresence>
                    </div>
                    </div>
                </div>

                {/* Steps indicator */}
                <LayoutGroup id="steps">
                    <motion.ul layout="size" className={styles.StepSection} initial={false}>
                    {pairs.map(({ date, time }, i) => {
                        const selected = selectedPairIndex === i;
                        return (
                        <motion.li
                            key={`${date}-${time}`}
                            layout
                            initial={false}
                            transition={{ backgroundColor: { duration: 0.25 } }}
                            className={styles.Step}
                            style={{
                            position: "relative",
                            listStyle: "none",
                            backgroundColor: "#E4E4E4",
                            }}
                        >
                            {selected && (
                            <motion.div
                                layoutId="underline"
                                style={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                bottom: 0,
                                height: "100%",
                                backgroundColor: "#CCD32A",
                                borderRadius: "10px",
                                zIndex: 3,
                                }}
                            />
                            )}
                        </motion.li>
                        );
                    })}
                    </motion.ul>
                </LayoutGroup>
                </div>

                <div className={styles.Nav}>
                <button className={styles.NavButton} onClick={goPrev} disabled={!hasPrev}>
                    <ChevronLeft />
                </button>

                <SecondaryButton
                    text={t("buttons.order")}
                    onClick={handleCartAdd}
                    showChevron={false}
                    colorVariant="neutral"
                    className={styles.NavSubmitButton}
                />

                <button className={styles.NavButton} onClick={goNext} disabled={!hasNext}>
                    <ChevronRight />
                </button>
                </div>
            </div>
            </div>
        )}
        </>
    );
};

export default PastOrderPopup;
